import { tabbable } from 'tabbable';

function getId(modal: Element): Nullable<string> {
	const match = modal.id.match(/^dialog:(dialog-\d+):positioner$/);
	return match !== null ? match![1] : null;
}

export function getAllIds(): string[] {
	return Array.from(document.body.children)
		.map(element => getId(element))
		.filter(id => id !== null);
}

export function getParent(el: Element): HTMLElement {

	if (!el) {
		throw Error(`${el} does not have a DOMNode. Maybe it's not mounted yet or its render method is returning null.`);
	}

	if (!el || !el.closest) {
		throw Error(`${el} is not a valid screen child. It does not have a closest method`);
	}

	const screen = el.closest('[id^="dialog:dialog-"][id$=":positioner"]');
	if (!screen) {
		throw Error(`${el} is not a valid screen child`);
	}

	return screen as HTMLElement;
}

// Indica si la vista (PosScreen o Dialog) que contiene el elemento es la vista en primer plano.
// OJO: En componentes nuevos no debería usarse este método, sino el hook **useIsOnTop**
export function legacy_isOnTop(screenChild: Element): boolean {
	const parentModalId = getId(getParent(screenChild) as Element);
	const allModalIds = getAllIds();
	return parentModalId === allModalIds[allModalIds.length - 1];
}

export function sendFocus(modalId: string) {
	// Si no lo hago con esto un timeout cuando cierro un dialogo no llega el foco
	setTimeout(() => document.getElementById(`dialog:${modalId}:content`)?.focus());
}

function getInitialFocusElement(content: HTMLElement): HTMLElement {
	const form = content.querySelector('form');
	const scope = (form instanceof HTMLElement) ? form : content;

	const tabbablesAll = tabbable(scope);
	const tabbablesHtml = tabbablesAll.filter(
		(el): el is HTMLElement => el instanceof HTMLElement
	);

	const firstField = tabbablesHtml.find((el) => {
		const tag = el.tagName;
		const dataScope = el.getAttribute('data-scope');

		if (tag === 'INPUT') {
			const input = el as HTMLInputElement;
			return input.type !== 'hidden' && !input.disabled;
		}

		if (tag === 'SELECT') return !(el as HTMLSelectElement).disabled;
		if (tag === 'TEXTAREA') return !(el as HTMLTextAreaElement).disabled;

		// Este dataScope es para un select de chakra, ya que no renderiza el componente html en un select normal de html dentro del formulario.
		if (dataScope === 'select') return !(el as HTMLDivElement).getAttribute('data-disabled');

		return false;
	});

	return firstField ?? tabbablesHtml[0] ?? scope;
}

export function sendInitialFocus(modalId: string) {
	// Chakra monta el contenido a través de portal/presence; esperamos al siguiente tick para asegurar que existe en el DOM.
	setTimeout(() => {
		const content = document.getElementById(`dialog:${modalId}:content`);
		if (content instanceof HTMLElement)
			getInitialFocusElement(content).focus();
	});
}
